import { Component, forwardRef, Input, OnDestroy, OnInit, EventEmitter, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { isObservable, Observable, Subject } from 'rxjs';
import { SelectControl } from '../../../../create-request/models/OptionDropdownSelect';
import { takeUntil } from 'rxjs/operators';
import { Constants, distributionReasonErrorMap } from '../../../utilities/Constants';

@Component({
  standalone: false,
  selector: 'mm-select-dropdown-control',
  templateUrl: './select-dropdown-control.component.html',
  styleUrls: ['./select-dropdown-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SelectDropdownControlComponent),
    }
  ]
})
export class SelectDropdownControlComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() selectModel!: SelectControl | Observable<SelectControl>;
  @Input() showSelectOne: boolean = true;
  @Input() showBlankOption: boolean = false;
  @Input() showIntlWireTooltip: boolean;
  @Input() tooltipVerbiage: string = '';
  @Input() defaultValue: FormControl;
  @Input() isAch: boolean = false;
  @Input() tooltipHtml: string = '';
  internalSelectModel: SelectControl;
  private destroy$ = new Subject<void>();
  @Input() isInternalTransfer: boolean;
  @Input() suppressEmit: boolean = false;
  @Output() defaultIRA: EventEmitter<boolean> = new EventEmitter();
  @Input() isIrsDropdown: boolean = false;
  @Input() irsPlaceholder: string = '';
 
  constructor() { }

  ngOnInit(): void {
    if (isObservable(this.selectModel)) {
      this.selectModel
        .pipe(takeUntil(this.destroy$))
        .subscribe((model) => {
          this.internalSelectModel = model;
          this.setDefaultSelection();
        })
    } else {
      this.internalSelectModel = this.selectModel;
      this.setDefaultSelection();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  setDefaultSelection(): void {
    if (this.internalSelectModel.options.length === 1) {
      this.control?.setValue(this.internalSelectModel.options[0].value,  { emitEvent: this.suppressEmit ? false : true }); //events are emitted by default
    }
    if (this.defaultValue?.value) {
      let index: number
      if (this.isAch && (this.internalSelectModel.options.findIndex(item => item.value.replace('-', '').replace(/\s/g, '') === this.defaultValue?.value.replace('-', '').replace(/\s/g, '')) !== -1)) {
        index = this.internalSelectModel.options.findIndex(item => item.value.replace('-', '').replace(/\s/g, '') === this.defaultValue?.value.replace('-', '').replace(/\s/g, ''));
      } else if (this.isAch && (this.internalSelectModel.options.findIndex(item => item.value === this.defaultValue?.value) === -1)) {
        index = this.internalSelectModel.options.findIndex(item => item.name === this.defaultValue?.value);
      } else if (this.isAch && (this.internalSelectModel.options.findIndex(item => item.value === this.defaultValue?.value) !== -1)) {
        index = this.internalSelectModel.options.findIndex(item => item.value === this.defaultValue?.value);
      } else {
        index = this.internalSelectModel.options.findIndex(item => item.value === this.defaultValue?.value);
      }
      this.control?.setValue(this.internalSelectModel.options[index]?.value, { emitEvent: this.suppressEmit ? false : true });

    }
    if (this.isInternalTransfer && (this.parentForm?.controls['iraContributionType'])) {
      this.defaultIRA.emit(this.parentForm?.controls[`${this.internalSelectModel?.controlName}`]?.value.includes(Constants.iraRolloverLabel) ?? false);
    }
    if (this.isIrsDropdown) {
      if (!this.irsPlaceholder) {
        if (this.internalSelectModel.controlName === 'taxYear') {
          this.irsPlaceholder = 'Select Year';
        } else if (this.internalSelectModel.controlName === 'taxMonth') {
          this.irsPlaceholder = 'Select Month';
        }
      }
    }
    if (this.control?.value === null){
      this.control?.setValue('', { emitEvent: this.suppressEmit ? false : true });
    }
  }

  get hasError(): boolean {
    if (this.control?.touched) {
      return this.parentForm?.controls[`${this.internalSelectModel?.controlName}`]?.invalid ?? false
    }
    return false;
  }

  get errorText(): string {
    if (this.control.errors && this.internalSelectModel.controlName === 'distributionReason') {
      const errorKeys: string[] = Object.keys(
        this.control.errors ?? {}
      );
      if ( errorKeys.length > 0) {
        return distributionReasonErrorMap[errorKeys[0] as keyof typeof distributionReasonErrorMap];
      }
    }
    return this.internalSelectModel.validationMessage;
  }

  get control(): UntypedFormControl {
    return this.parentForm?.get(this.internalSelectModel?.controlName) as UntypedFormControl;
  }
}
